import { api } from '../api';

export type AddCardToDigestMode = 'single' | 'split';

export type AddCardToDigestResponse = {
  mode: AddCardToDigestMode;
  added: number;
  alreadyAdded: number;
};

export type AddCardToDigestPayload = {
  cardId: number;
  emailDigestId?: number;
  mode?: AddCardToDigestMode;
};

export type AddAnswerToDigestPayload = {
  // Opaque id assigned by the Ask Klue service. In modern-chat this is the
  // assistant message id; Klue's backend treats it as pure provenance /
  // idempotence key and does not validate the format.
  answerId: string;
  answerHtml: string;
  emailDigestId?: number;
  // Item title fallback for single-mode (no <section data-article-url>
  // markers in answerHtml). If omitted, backend uses userQuestion, then
  // "Ask Klue Answer".
  title?: string;
  userQuestion?: string;
};

// Backend response shape is identical for both content sources.
export type AddAnswerToDigestResponse = AddCardToDigestResponse;

// The digest LIST is owned by the intelDigest Rematch model — see
// `src/store/models/intel-digest/intelDigest.model.ts`. This slice
// intentionally has no getDigests endpoint; the DigestPickerModal reads
// from `state.intelDigest.allDigests`, warmed by
// `dispatch.intelDigest.fetchAllDigests()` on modal mount (and also
// by KlueCardMenu at menu-open for the card path).
const digestsApi = api.injectEndpoints({
  endpoints: (build) => ({
    addCardToDigest: build.mutation<
      AddCardToDigestResponse,
      AddCardToDigestPayload
    >({
      query: ({ cardId, emailDigestId, mode }) => ({
        url: `/cards/${cardId}/add_to_digest.json`,
        method: 'POST',
        body: {
          emailDigestId,
          mode,
        },
      }),
    }),

    // Adds an Ask Klue answer directly to a digest, bypassing the card
    // intermediary. Backend infers split-vs-single from the presence of
    // <section data-article-url> markers in `answerHtml`. Idempotence is
    // per-(answerId, emailDigestId) — same shape as the card path.
    addAnswerToDigest: build.mutation<
      AddAnswerToDigestResponse,
      AddAnswerToDigestPayload
    >({
      query: ({
        answerId,
        answerHtml,
        emailDigestId,
        title,
        userQuestion,
      }) => ({
        url: `/ask_klue_answers/add_to_digest.json`,
        method: 'POST',
        body: {
          answerId,
          answerHtml,
          emailDigestId,
          title,
          userQuestion,
        },
      }),
    }),
  }),
});

export const { useAddCardToDigestMutation, useAddAnswerToDigestMutation } =
  digestsApi;
